import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { Card } from '@neo/primitives/card'
import { Tooltip } from '@neo/primitives/tooltip'
import { Typography } from '@neo/primitives/typography'
import { VirtualizedScrollArea } from '@neo/primitives/virtualized-scroll-area'
import { motion } from 'framer-motion'
import { ChevronsDownUp, ChevronsUpDown, Minus, X } from 'lucide-react'
import { useRef } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { useFlowStore } from 'v2/pages/flow/store'
import { useScrollBehavior } from 'v2/shared/hooks/use-scroll-behavior'
import { PrimaryEvents, primaryEventBus } from 'v2/utils/event-bus/primary'
import { useFlowRunStore } from '../store'

interface PanelWrapperProps<T = unknown> {
  title: React.ReactNode
  subtitle?: React.ReactNode
  headerBgColor?: string
  headerTextColor?: string
  children?: React.ReactNode
  toggleButton?: React.ReactNode
  className?: string
  footer?: React.ReactNode
  onClose?: () => void
  // Virtualization props
  data?: T[]
  itemContent?: (index: number, item: T) => React.ReactNode
  // Width configuration
  width?: {
    minimized: number
    normal: number
  }
}

export const PanelWrapper = <T,>({
  title,
  subtitle,
  headerBgColor = 'bg-card',
  headerTextColor = 'text-foreground',
  children,
  toggleButton,
  className,
  footer,
  onClose,
  data,
  itemContent,
  width = { minimized: 280, normal: 384 },
}: PanelWrapperProps<T>) => {
  const panelState = useFlowRunStore((state) => state.panelState)
  const setPanelState = useFlowRunStore((state) => state.setPanelState)
  const scrollViewportRef = useRef<HTMLDivElement>(null)
  const setShowVibeSidebar = useFlowStore((state) => state.setShowVibeSidebar)

  const handleMinimize = () => {
    setPanelState('minimized')
  }

  const handleMaximize = () => {
    setPanelState('maximized')
    setShowVibeSidebar(false)
  }

  const handleRestore = () => {
    setPanelState('normal')
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    } else {
      primaryEventBus.emit(PrimaryEvents.HANGUP_CALL)
    }
  }

  // Use virtualization if data and itemContent are provided
  const useVirtualization = data && itemContent

  // Use the existing scroll behavior hook for non-virtualized content
  let scrollChunks: React.ReactNode[] | undefined
  if (useVirtualization) {
    scrollChunks = undefined
  } else if (children) {
    scrollChunks = [children]
  } else {
    scrollChunks = []
  }

  const { hasUserScrolled } = useScrollBehavior({
    scrollViewportRef,
    chunks: scrollChunks,
  })

  // For virtualized content, use followOutput with user scroll detection
  const followOutput = useVirtualization ? () => !hasUserScrolled : undefined

  const content = useVirtualization ? (
    <Virtuoso
      data={data}
      itemContent={itemContent}
      style={{ height: '100%' }}
      followOutput={followOutput}
      alignToBottom
      atBottomThreshold={20}
      initialTopMostItemIndex={Math.max(0, (data?.length || 1) - 1)}
    />
  ) : (
    children
  )

  // Calculate height for animation
  const getHeight = () => {
    if (panelState === 'minimized') return 44
    if (panelState === 'maximized') return 'calc(100vh - 190px)'
    return 470
  }

  // Calculate width for animation
  const getWidth = () => {
    if (panelState === 'minimized') return width.minimized
    return width.normal
  }

  return (
    <motion.div
      animate={{
        y: 0,
        x: 0,
      }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
        mass: 0.8,
      }}
      className={cn(className)}
    >
      <motion.div
        animate={{
          height: getHeight(),
          width: getWidth(),
        }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30,
          mass: 0.8,
        }}
        style={{ maxWidth: `${width.normal}px` }}
        className="flex flex-col rounded shadow-none overflow-hidden"
      >
        <Card className="flex flex-col rounded border-border bg-card font-inter shadow-none overflow-hidden h-full">
          {/* Header */}
          <div
            className={cn(
              'flex items-center justify-between gap-2 border-b border-st2 px-3 py-2',
              headerBgColor,
              headerTextColor,
            )}
          >
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {toggleButton}
              {typeof title === 'string' ? (
                <Typography variant="H4/500" className={headerTextColor}>
                  {title}
                </Typography>
              ) : (
                title
              )}
              {subtitle && (
                <span className="font-inter text-xs-400 text-secondary">{subtitle}</span>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1 text-secondary">
              {panelState !== 'minimized' && (
                <Tooltip content="Minimize">
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn('h-7 w-7', 'text-secondary hover:bg-muted')}
                    onClick={handleMinimize}
                    aria-label="Minimize panel"
                    data-testid="button-minimize"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                </Tooltip>
              )}
              <Tooltip content={panelState === 'maximized' ? 'Restore' : 'Maximize'}>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn('h-7 w-7', 'text-secondary hover:bg-muted')}
                  onClick={panelState === 'maximized' ? handleRestore : handleMaximize}
                  aria-label={panelState === 'maximized' ? 'Restore panel' : 'Maximize panel'}
                  data-testid="button-maximize"
                >
                  {panelState === 'maximized' ? (
                    <ChevronsDownUp className="h-4 w-4" />
                  ) : (
                    <ChevronsUpDown className="h-4 w-4" />
                  )}
                </Button>
              </Tooltip>
              <Tooltip content="Close">
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn('h-7 w-7', 'text-secondary hover:bg-muted')}
                  onClick={handleClose}
                  aria-label="Close panel"
                  data-testid="button-close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </Tooltip>
            </div>
          </div>

          {/* Content Area */}
          {panelState !== 'minimized' && (
            <>
              {useVirtualization ? (
                <div className="min-h-0 flex-1 px-2 py-3" ref={scrollViewportRef}>
                  {content}
                </div>
              ) : (
                <VirtualizedScrollArea className="min-h-0 flex-1 px-2 py-3" ref={scrollViewportRef}>
                  {content}
                </VirtualizedScrollArea>
              )}
              {footer && <div className="mt-auto">{footer}</div>}
            </>
          )}
        </Card>
      </motion.div>
    </motion.div>
  )
}
